import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

const api = axios.create({
  baseURL: config.API_BASE_URL || "http://localhost:5002/api/v1.0",
  headers: { "Content-Type": "application/json" },
});

// ─── Options ──────────────────────────────────────────────────────────
const CATEGORY_OPTIONS = [
  "Kennel Boarding",
  "Grooming",
  "Pet Vet Clinic",
  "Pet Shop",
  "Other",
];

// ─── Component ──────────────────────────────────────────────────────
const PublicUserForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    companyName: "",
    location: "",
    category: [],
    plan: [], // will be auto‑filled
  });
  const [otherRequirement, setOtherRequirement] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // ─── Auto‑generate plan based on categories ──────────────────────
 useEffect(() => {
   const cats = formData.category;
   let plan = "";

   // Case 1: Only "Other" selected → Free plan
   if (cats.includes("Other") && cats.length === 1) {
     plan = "Free plan";
   }
   // Case 2: "Other" plus other categories → ignore "Other"
   else {
     const otherCats = cats.filter((c) => c !== "Other");
     if (otherCats.includes("Pet Vet Clinic")) {
       plan = "Care plan";
     } else if (otherCats.includes("Pet Shop")) {
       plan = "Fetch plan";
     } else if (
       otherCats.some((c) => c === "Kennel Boarding" || c === "Grooming")
     ) {
       plan = "Grooming Boarding plan";
     }
     // else plan stays "" (no relevant category)
   }

   setFormData((prev) => ({
     ...prev,
     plan: plan ? [plan] : [],
   }));
 }, [formData.category]);

  // ─── Handlers ──────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => {
      const current = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...current, value] };
      } else {
        return { ...prev, [name]: current.filter((item) => item !== value) };
      }
    });
  };

  const handleOtherChange = (e) => {
    setOtherRequirement(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!formData.phone.trim()) {
      setError("Phone number is required.");
      return;
    }
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      setError("Phone number must be exactly 10 digits.");
      return;
    }
    if (formData.category.includes("Other") && !otherRequirement.trim()) {
      setError("Please specify your requirement when selecting 'Other'.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        companyName: formData.companyName.trim() || "",
        location: formData.location.trim() || "",
        category: formData.category || [],
        plan: formData.plan || [],
        otherRequirement: otherRequirement.trim() || "",
      };

      await api.post("/publicuser/addpublicform", payload);
      setSuccess(true);
    } catch (err) {
      console.error("Submission error:", err);
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setFormData({
      name: "",
      phone: "",
      companyName: "",
      location: "",
      category: [],
      plan: [],
    });
    setOtherRequirement("");
    setError(null);
  };

  // ─── JSX ────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {!success && (
          <>
            <h2 style={styles.title}>📝 Contact Form</h2>
            <p style={styles.subtitle}>Fill in your details.</p>
          </>
        )}

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        {success ? (
          <div style={styles.thankYou}>
            <div style={styles.thankYouIcon}>🎉</div>
            <h3 style={styles.thankYouTitle}>Thank you for registering!</h3>
            <p style={styles.thankYouText}>
              We’ve received your details and will reach out to you shortly.
            </p>
            
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Name */}
            <div style={styles.field}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                required
                style={styles.input}
              />
            </div>

            {/* Phone */}
            <div style={styles.field}>
              <label style={styles.label}>
                Phone Number (WhatsApp preferred) *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g. 9876543210"
                required
                style={styles.input}
              />
              <span style={styles.hint}>Must be exactly 10 digits.</span>
            </div>

            {/* Company */}
            <div style={styles.field}>
              <label style={styles.label}>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your organisation"
                style={styles.input}
              />
            </div>

            {/* Location */}
            <div style={styles.field}>
              <label style={styles.label}>Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State"
                style={styles.input}
              />
            </div>

            {/* Category – checkboxes */}
            <div style={styles.field}>
              <label style={styles.label}>Category (choose one or more)</label>
              <div style={styles.checkboxGroup}>
                {CATEGORY_OPTIONS.map((opt) => (
                  <label key={opt} style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="category"
                      value={opt}
                      checked={formData.category.includes(opt)}
                      onChange={handleCheckboxChange}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            {/* Other requirement – appears only when "Other" is selected */}
            {formData.category.includes("Other") && (
              <div style={styles.field}>
                <label style={styles.label}>
                  Please tell us what you need *
                </label>
                <textarea
                  value={otherRequirement}
                  onChange={handleOtherChange}
                  placeholder="Describe your requirement…"
                  rows="3"
                  style={{ ...styles.input, resize: "vertical" }}
                />
              </div>
            )}

            {/* Plan – auto‑generated, read‑only */}
            <div style={styles.field}>
              <label style={styles.label}>Selected Plan</label>
              <div
                style={{
                  ...styles.input,
                  background: "#f8f9fa",
                  cursor: "default",
                  color: "#495057",
                }}
              >
                {formData.plan.length > 0 ? formData.plan[0] : "–"}
              </div>
              <span style={styles.hint}>
                Plan is automatically chosen based on your category selection.
              </span>
            </div>

            <button type="submit" disabled={submitting} style={styles.button}>
              {submitting ? "Submitting…" : "Register Now"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh",
    background: "#f0f2f5",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    maxWidth: "560px",
    width: "100%",
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    padding: "40px 36px",
  },
  title: {
    margin: "0 0 4px 0",
    fontSize: "26px",
    fontWeight: 600,
    color: "#1a1a2e",
  },
  subtitle: {
    margin: "0 0 24px 0",
    color: "#6c757d",
    fontSize: "15px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#343a40",
    marginBottom: "4px",
  },
  input: {
    padding: "10px 14px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #ced4da",
    transition: "border-color 0.15s ease",
    outline: "none",
  },
  hint: {
    fontSize: "12px",
    color: "#6c757d",
    marginTop: "4px",
  },
  checkboxGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "4px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    cursor: "pointer",
  },
  button: {
    padding: "12px 24px",
    background: "#4a6cf7",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.2s",
    marginTop: "8px",
  },
  errorBox: {
    background: "#f8d7da",
    color: "#721c24",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  thankYou: {
    textAlign: "center",
    padding: "20px 0 10px",
  },
  thankYouIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  thankYouTitle: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#1a1a2e",
    margin: "0 0 8px",
  },
  thankYouText: {
    fontSize: "15px",
    color: "#6c757d",
    margin: "0 0 24px",
  },
  closeButton: {
    padding: "10px 32px",
    background: "#4a6cf7",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.2s",
  },
};

export default PublicUserForm;
